import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CallbackService {
  id: string;
  name: string;
}

export interface SubmitCallbackInput {
  organization_id: string | undefined;
  name: string;
  phone: string;
  preferred_time: string;
  notes: string | null;
}

/**
 * Active services for the CallbackDialog's "booking" variant service picker.
 * Only fetched when the dialog is open and variant === "booking", controlled
 * via the `enabled` flag passed from the component.
 */
export function useCallbackServices(orgId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['callback-services', orgId],
    enabled: Boolean(orgId) && enabled,
    queryFn: async (): Promise<CallbackService[]> => {
      const { data, error } = await supabase
        .from('services')
        .select('id, name')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      return data ?? [];
    },
  });
}

/**
 * Insert a callback request submitted via the CallbackDialog form.
 */
export function useSubmitCallbackRequest() {
  return useMutation({
    mutationFn: async (input: SubmitCallbackInput): Promise<void> => {
      const { error } = await supabase
        .from('callback_requests')
        .insert({
          organization_id: input.organization_id,
          name: input.name,
          phone: input.phone,
          preferred_time: input.preferred_time,
          notes: input.notes,
        });

      if (error) throw error;
    },
  });
}
